// Aggregate progress across every file in an upload batch.
//
// Pure so it can be tested without mounting the modal: the modal holds
// `uploadStatus` and `uploadProgress` as index-keyed maps, and this turns them
// into the one line a user actually wants ("233/600, 41%").
//
// Percentage is BYTE-WEIGHTED, not count-based. With 600 similar JPEGs the two
// agree, but a 2 GB LAS beside small files makes a count-based bar sit near zero
// for minutes and then jump. Weighting by bytes keeps it honest.

/** Terminal states — the file is no longer being worked on, whatever the outcome. */
export const SETTLED_STATUSES = ['success', 'error', 'cancelled', 'skipped'];
const FAILED_STATUSES = ['error', 'cancelled'];

/**
 * @param {Array<{size?: number}>} files          selectedFiles, in order
 * @param {Record<number, string>} uploadStatus   index -> status
 * @param {Record<number, number>} uploadProgress index -> 0..100
 * @returns {{
 *   total: number, settled: number, failed: number,
 *   percent: number, activeIndex: number|null, started: boolean,
 * }}
 */
export default function computeOverallProgress(files, uploadStatus = {}, uploadProgress = {}) {
  const list = Array.isArray(files) ? files : [];
  const total = list.length;
  if (total === 0) {
    return { total: 0, settled: 0, failed: 0, percent: 0, activeIndex: null, started: false };
  }

  let settled = 0;
  let failed = 0;
  let doneBytes = 0;
  let totalBytes = 0;
  let activeIndex = null;
  let started = false;

  list.forEach((file, i) => {
    const size = Number(file?.size) || 0;
    totalBytes += size;
    const status = uploadStatus[i] || 'pending';

    if (status !== 'pending') started = true;

    if (SETTLED_STATUSES.includes(status)) {
      settled += 1;
      if (FAILED_STATUSES.includes(status)) failed += 1;
      // A settled file contributes its whole size regardless of outcome: the
      // bar tracks work COMPLETED, not bytes successfully stored. Otherwise a
      // batch with failures could never reach 100% and would look hung.
      doneBytes += size;
      return;
    }

    if (status === 'uploading' || status === 'finalizing' || status === 'paused') {
      if (activeIndex === null) activeIndex = i;
      const pct = Number(uploadProgress[i]);
      if (Number.isFinite(pct) && pct > 0) doneBytes += size * Math.min(pct, 100) / 100;
    }
  });

  // Zero-byte files are legal, so guard the divide. Falling back to the count
  // keeps the bar meaningful instead of showing NaN or a stuck 0%.
  const percent = totalBytes > 0
    ? Math.min(100, Math.round((doneBytes / totalBytes) * 100))
    : Math.round((settled / total) * 100);

  return { total, settled, failed, percent, activeIndex, started };
}
